import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MonoTypeOperatorFunction, tap } from 'rxjs';
import {
  baseUrl,
  ENDPOINTS,
} from '../../../../common/constants/endpoints.constants';
import { ApiResponse } from '../../../../common/models/api-response.model';
import {
  CustomWorkflow,
  CustomWorkflowFieldOptions,
  CustomWorkflowListItem,
  FieldAccess,
  Localized,
} from './custom-workflow.model';
import { WorkflowKind } from '../workflow.model';
import { ReflectionsService } from '../reflections/reflections.service';

type Created = { data: { id: number } };

@Injectable({ providedIn: 'root' })
export class CustomWorkflowService {
  private readonly http = inject(HttpClient);
  private readonly url = ENDPOINTS.Dashboard.Workflow.CustomWorkflows.Base;
  private readonly reflections = inject(ReflectionsService);

  list(page: number, perPage: number) {
    const params = new HttpParams()
      .append('page', page)
      .append('per_page', perPage);
    return this.http.get<ApiResponse<CustomWorkflowListItem>>(this.url, {
      params,
    });
  }

  fieldOptions() {
    return this.http.get<{ data: CustomWorkflowFieldOptions }>(
      `${this.url}/field-options`
    );
  }

  categories() {
    const params = new HttpParams().append('per_page', 100);
    return this.http.get<{ data: { value: number; label: string }[] }>(
      `${baseUrl}/lookup/flow-template-categories`,
      { params }
    );
  }

  create(body: {
    name: Localized;
    description?: Localized;
    category_id: number;
    icon?: number | null;
  }) {
    return this.http.post<Created>(this.url, body);
  }

  show(id: number) {
    return this.http.get<{ data: CustomWorkflow }>(
      ENDPOINTS.Dashboard.FlowTemplates.Details.replace('{id}', `${id}`)
    );
  }

  update(id: number, body: Record<string, unknown>, kind: WorkflowKind) {
    const url =
      kind === 'built_in'
        ? ENDPOINTS.Dashboard.FlowTemplates.Details.replace('{id}', `${id}`)
        : `${this.url}/${id}`;
    return this.http.put(url, body);
  }

  remove(id: number) {
    return this.http.delete(`${this.url}/${id}`);
  }

  preview(id: number) {
    return this.http.get<{ data: { sections: any[] } }>(
      `${this.url}/${id}/preview`
    );
  }

  setActive(id: number, isActive: boolean) {
    return this.http.post(`${this.url}/${id}/active`, { is_active: isActive });
  }

  duplicate(id: number) {
    return this.http.post<Created>(`${this.url}/${id}/duplicate`, {});
  }

  createSection(id: number, label: Localized) {
    return this.http
      .post<Created>(`${this.url}/${id}/sections`, { label })
      .pipe(this.invalidatesFields(id));
  }

  updateSection(id: number, sectionId: number, label: Localized) {
    return this.http
      .put(`${this.url}/${id}/sections/${sectionId}`, { label })
      .pipe(this.invalidatesFields(id));
  }

  deleteSection(id: number, sectionId: number) {
    return this.http
      .delete(`${this.url}/${id}/sections/${sectionId}`)
      .pipe(this.invalidatesFields(id));
  }

  reorderSections(id: number, ids: number[]) {
    return this.http
      .put(`${this.url}/${id}/sections/reorder`, { ids })
      .pipe(this.invalidatesFields(id));
  }

  createField(id: number, body: Record<string, unknown>) {
    return this.http
      .post<Created>(`${this.url}/${id}/fields`, body)
      .pipe(this.invalidatesFields(id));
  }

  updateField(id: number, fieldId: number, body: Record<string, unknown>) {
    return this.http
      .put(`${this.url}/${id}/fields/${fieldId}`, body)
      .pipe(this.invalidatesFields(id));
  }

  deleteField(id: number, fieldId: number) {
    return this.http
      .delete(`${this.url}/${id}/fields/${fieldId}`)
      .pipe(this.invalidatesFields(id));
  }

  reorderFields(id: number, sectionId: number, ids: number[]) {
    return this.http
      .put(`${this.url}/${id}/fields/reorder`, {
        section_id: sectionId,
        ids,
      })
      .pipe(this.invalidatesFields(id));
  }

  /** A section or field change alters what reflections can read: their cached fields go once it succeeds. */
  private invalidatesFields<T>(id: number): MonoTypeOperatorFunction<T> {
    return tap(() => this.reflections.invalidateFields(id));
  }

  createStep(id: number, body: { name: Localized; position?: number }) {
    return this.http.post<Created>(`${this.url}/${id}/steps`, body);
  }

  updateStep(
    id: number,
    stepId: number,
    body: { name?: Localized; field_access?: Record<string, FieldAccess> }
  ) {
    return this.http.put(`${this.url}/${id}/steps/${stepId}`, body);
  }

  deleteStep(id: number, stepId: number) {
    return this.http.delete(`${this.url}/${id}/steps/${stepId}`);
  }

  reorderSteps(id: number, ids: number[]) {
    return this.http.put(`${this.url}/${id}/steps/reorder`, { ids });
  }

  createRequirement(id: number, value: Localized) {
    return this.http.post<Created>(`${this.url}/${id}/requirements`, { value });
  }

  updateRequirement(id: number, requirementId: number, value: Localized) {
    return this.http.put(`${this.url}/${id}/requirements/${requirementId}`, {
      value,
    });
  }

  deleteRequirement(id: number, requirementId: number) {
    return this.http.delete(`${this.url}/${id}/requirements/${requirementId}`);
  }
}
